import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileText, CheckCircle } from "lucide-react";

const PolicyAgreement = ({ onAgree, onCancel, clientName }) => {
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [signature, setSignature] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreedToPolicy || !signature) {
      return;
    }
    onAgree({ signature, date });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full my-8 shadow-2xl">
        <div className="bg-[#D4AF37] text-white p-6 rounded-t-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Service Agreement & Policy</h2>
              <p className="text-white/90 text-sm">Please review and sign to complete your booking</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Policy Content */}
          <div className="bg-[#F9F8F6] p-6 rounded-lg border border-[#F2EBE5] max-h-96 overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-[#1A1A1A]">Service Terms & Conditions</h3>
            <div className="space-y-4 text-[#1A1A1A] leading-relaxed">
              <p>
                By securing your appointment with The Lux Standard, you acknowledge and agree to the following service policies:
              </p>

              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold mb-1">Hair Preparation</h4>
                  <p className="text-sm">
                    Clients must arrive with clean, fully dry hair unless otherwise arranged. Wet or damp hair will result in a $40 blow-dry service added to your total.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Payment Terms</h4>
                  <p className="text-sm">
                    All remaining balances are due in full prior to the start of services. We accept all major payment methods.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Cancellation Policy</h4>
                  <p className="text-sm">
                    Cancellations made within 72 hours of your scheduled appointment, as well as no-shows, will be charged 100% of the service total.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Punctuality</h4>
                  <p className="text-sm">
                    Appointments are subject to a $50 late fee for every 20 minutes past the scheduled start time. Services will conclude at the original end time, with the artist completing as much as possible within the remaining timeframe.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Health & Safety</h4>
                  <p className="text-sm">
                    For the safety and comfort of all clients, we reserve the right to refuse service to anyone who is ill, contagious, or under the influence of drugs or alcohol.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Medical Disclosure</h4>
                  <p className="text-sm">
                    All allergies, sensitivities, or medical concerns must be disclosed at the time of booking to ensure your safety and comfort.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-1">Additional Fees</h4>
                  <p className="text-sm">
                    Early morning services, holiday bookings, and parking fees may apply. When applicable, these fees will be communicated during booking and collected on the day of service unless prepaid.
                  </p>
                </div>
              </div>

              <p className="mt-6 text-sm italic">
                These policies ensure we maintain the highest standards of service while respecting everyone's time and commitments.
              </p>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-[#F2EBE5] rounded-lg">
            <Checkbox
              id="policy-agree"
              checked={agreedToPolicy}
              onCheckedChange={setAgreedToPolicy}
              className="mt-1"
            />
            <Label htmlFor="policy-agree" className="cursor-pointer leading-relaxed">
              I have read, understood, and agree to all terms and conditions outlined in The Lux Standard Service Agreement & Policy.
            </Label>
          </div>

          {/* Signature Fields */}
          <div className="space-y-4 pt-4 border-t">
            <div>
              <Label htmlFor="signature" className="text-base font-semibold">Digital Signature *</Label>
              <p className="text-xs text-[#8C8C8C] mb-2">Type your full name as it appears on your ID</p>
              <Input
                id="signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Your Full Name"
                required
                className="font-serif text-lg italic border-b-2 border-[#D4AF37] focus:border-[#b89530] rounded-none px-0"
              />
            </div>

            <div>
              <Label htmlFor="date" className="text-base font-semibold">Date *</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-48"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6">
            <Button
              type="submit"
              disabled={!agreedToPolicy || !signature}
              className="flex-1 bg-[#D4AF37] hover:bg-[#b89530] text-white py-6 text-lg"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Sign & Complete Booking
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="px-8 py-6"
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs text-center text-[#8C8C8C]">
            By signing, you acknowledge this agreement is legally binding.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PolicyAgreement;